import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import getConfig from 'next/config';
import i18next from 'i18next';
import classNames from 'classnames';

import { package as PackagePropType } from 'lib/CustomPropTypes';
import { isImage } from 'lib/getAimsUrl';
import { getAdLevelForPID, getPlaymakerById, playmakerConfiguration } from 'lib/playmaker';

import Dek from 'components/Dek';
import PlaymakerPlayer from 'components/PlaymakerPlayer';
import VideoSlate from 'components/VideoPlayer/VideoSlate';

import getMetadata from './utils/getMetadata';
import isAutoplay from './utils/isAutoplay';
import { NEWS_NOW_DEFAULT_SLATE, TODAY_ALL_DAY_DEFAULT_SLATE } from './utils/constants';

const { publicRuntimeConfig } = getConfig();

class Playmaker extends React.Component {
  static propTypes = {
    content: PackagePropType.isRequired,
    pkgClassName: PropTypes.string,
    handleStatus: PropTypes.func,
    hasClickedToPlay: PropTypes.bool,
    onPlayClick: PropTypes.func,
    styleOverride: PropTypes.shape({
      content: PropTypes.string,
      info: PropTypes.string,
      title: PropTypes.string,
      dek: PropTypes.string,
    }),
  };

  static contextTypes = {
    vertical: PropTypes.string,
  };

  static defaultProps = {
    pkgClassName: 'pkg playmaker',
    handleStatus: Function.prototype,
    onPlayClick: Function.prototype,
    styleOverride: null,
    hasClickedToPlay: false,
  }

  constructor(props) {
    super(props);
    this.state = {
      isLive: false,
      hasClickedToPlay: false,
    };
  }

  componentDidMount() {
    this.getStatus();
  }

  onPlayClick = () => {
    const { onPlayClick } = this.props;
    if (onPlayClick) onPlayClick();

    this.setState({ hasClickedToPlay: true });
  };

  onVideoComplete = () => {
    this.getStatus();
  };

  getStatus = () => {
    const { handleStatus, content: { metadata } } = this.props;
    const { vertical } = this.context;
    getPlaymakerById(getMetadata(metadata, vertical).playmakerId)
      .then((playmaker) => {
        const { status, isLive } = playmaker;
        // Update state
        this.setState({ isLive });

        // Update parent status state
        handleStatus(status, isLive);
      });
  };

  handleError = (err) => {
    if (typeof window !== 'undefined' && window.console) {
      window.console.log(err);
    }
  };

  render() {
    const {
      content: { metadata },
      hasClickedToPlay: hasClickedToPlayProps,
      pkgClassName,
      styleOverride,
    } = this.props;
    const { isLive, hasClickedToPlay: hasClickedToPlayState } = this.state;
    const { vertical } = this.context;

    const {
      playmakerArt,
      playmakerAutoplayMuted,
      playmakerDescription,
      playmakerId,
      playmakerPlayBehavior,
      playmakerTitle,
      playmakerFlag,
    } = getMetadata(metadata, vertical);

    const defaultSlateImage = vertical === 'today' ? TODAY_ALL_DAY_DEFAULT_SLATE : NEWS_NOW_DEFAULT_SLATE;
    const adLevel = getAdLevelForPID(playmakerId, publicRuntimeConfig);
    const leapConfigUrl = get(playmakerConfiguration, `${vertical}.leapConfigUrl`, undefined);
    const poster = get(playmakerConfiguration, `${vertical}.poster`, undefined);
    const csid = get(playmakerConfiguration, `${vertical}.csid`, undefined);
    const sfid = get(playmakerConfiguration, `${vertical}.sfid`, undefined);
    const afid = get(playmakerConfiguration, `${vertical}.afid`, undefined);
    const metr = get(playmakerConfiguration, `${vertical}.metr`, undefined);
    const resource = get(playmakerConfiguration, `${vertical}.resource`, undefined);
    const ignoredSCTEAdMessageTypes = get(playmakerConfiguration, `${vertical}.ignoredSCTEAdMessageTypes`, undefined);
    const cdnTokenEndpoint = get(playmakerConfiguration, `${vertical}.cdnTokenEndpoint`, undefined);
    const autoplay = playmakerPlayBehavior === 'autoplay';

    const isVideoPlaying = hasClickedToPlayState || hasClickedToPlayProps || isAutoplay(metadata);

    const hasPlaymakerId = !!playmakerId;
    const isAutoplayMuted = playmakerAutoplayMuted !== false; // default play muted

    // If user defined the playmaker art as a static image, use that instead of preview
    const isArtImageUrl = playmakerArt && isImage(playmakerArt);

    return (
      <section className={`${pkgClassName} Playmaker`}>
        <article className={styleOverride && styleOverride.content}>
          <div className="videoOuter">
            <div className="videoInner">
              {hasPlaymakerId && isVideoPlaying && (
                <PlaymakerPlayer
                  adLevel={adLevel}
                  autoplay={autoplay}
                  autoplayMuted={isAutoplayMuted}
                  ignoredSCTEAdMessageTypes={ignoredSCTEAdMessageTypes}
                  isLive={isLive}
                  leapConfigUrl={leapConfigUrl}
                  onVideoComplete={this.onVideoComplete}
                  csid={csid}
                  sfid={sfid}
                  afid={afid}
                  metr={metr}
                  poster={poster}
                  resource={resource}
                  cdnTokenEndpoint={cdnTokenEndpoint}
                  pid={parseInt(playmakerId, 10) || playmakerId}
                />
              )}
              {hasPlaymakerId && !isVideoPlaying && (
                <VideoSlate
                  {...this.props}
                  isLive={isLive}
                  pageView="front"
                  video={{
                    preview: {
                      url: playmakerArt,
                      playsInline: true,
                      autoplay: true,
                    },
                    primaryImage: {
                      url: {
                        primary: isArtImageUrl ? playmakerArt : defaultSlateImage,
                      },
                    },
                    playable: true,
                  }}
                  onPlayClick={this.onPlayClick}
                />
              )}
            </div>
          </div>
          {styleOverride && (
            <div className={styleOverride.info}>
              <h2 className={`tease-card__title ${styleOverride.title} title`}>
                {
                  isLive ? <span className="Playmaker__flag-dot" /> : null
                }
                <span className={classNames('Playmaker__flag', { 'Playmaker__flag--live': isLive })}>
                  {`${playmakerFlag || i18next.t('Watch live')} / `}
                </span>
                {playmakerTitle}
              </h2>
              <Dek dek={playmakerDescription} styles={styleOverride.dek} />
            </div>
          )}
        </article>
      </section>
    );
  }
}

export default Playmaker;
